import 'pdfjs-dist/legacy/build/pdf.worker'

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Block } from '@aws-sdk/client-textract'
import { TranslateService } from '@ngx-translate/core'
import * as pdfjsLib from 'pdfjs-dist'
import { PDFPageProxy, RenderTask } from 'pdfjs-dist'
import { BehaviorSubject, catchError, combineLatest, combineLatestWith, debounceTime, from, map, merge, Observable, of, startWith, Subject, takeUntil, withLatestFrom } from 'rxjs'

export interface SelectedPagesEventData {
  selectedPages: Set<number>
  totalPages: number
}

export interface PdfPage {
  pageNum: number
  pdfPage: PDFPageProxy
  thumbnail: string
  label: string
  color: string
  tooltip?: string
}

export interface PdfPageData {
  label: string
  tooltip?: string
  color: string
  pageNum: number
  isActive?: boolean
}

/** Horizontal only: the vertical keys still have to scroll a page taller than the window in Fill mode. */
const PAGE_STEP_BY_KEY: Record<string, number> = {
  ArrowRight: 1,
  ArrowLeft: -1,
}

const TEXT_ENTRY_TAGS = ['INPUT', 'TEXTAREA', 'SELECT']

@Component({
  selector: 'mmx-pdf-reader',
  templateUrl: './pdf-reader.component.html',
  styleUrls: ['./pdf-reader.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PdfReaderComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('pdfCanvas', { read: ElementRef })
  public canvasRef: ElementRef<HTMLCanvasElement> | undefined

  @Input()
  public url!: string

  @Input()
  public page = 1

  @Input()
  public blocks: Block[] = []

  @Input()
  public numberOfPages = 0

  @Input()
  public pagesData: PdfPageData[] = []

  @Input()
  public blocksLoading = false

  @Input()
  public thumbnailsVisible = true

  @Input()
  public fillMode: 'fit' | 'fill' = 'fit'

  @Output()
  public init = new EventEmitter<void>()

  @Output()
  public activePageChange = new EventEmitter<number>()

  @Output()
  public selectionChange = new EventEmitter<SelectedPagesEventData>()

  @HostBinding('class.fill-mode-fill')
  public get fillModeFill() {
    return this.fillMode === 'fill'
  }

  /** Focusable so the arrow keys drive the viewer the user last touched, not every one on screen. */
  @HostBinding('attr.tabindex')
  public readonly tabindex = 0

  public pages$!: Observable<PdfPage[]>
  public currentPage = 1
  public selectedPages$ = new BehaviorSubject<Set<number>>(new Set())
  public width?: number
  public height?: number

  public loading$ = new BehaviorSubject<boolean>(true)
  public loadError$ = new BehaviorSubject<boolean>(false)
  public blocksLoading$ = new BehaviorSubject<boolean>(false)
  public resize$ = new Subject<Event>()
  public shouldShowLoadingThumbnals$!: Observable<boolean>
  private loadedPages: PdfPage[] = []
  private relabelledPages$ = new Subject<PdfPage[]>()
  private renderTask?: RenderTask

  public get pageNumbers () {
    return Array.from({ length: this.numberOfPages }, (_, i) => i + 1)
  }

  /** Textract progress is reported separately and can outlive a document that never rendered. */
  public progressVisible$ = combineLatest([this.loading$, this.loadError$, this.blocksLoading$])
    .pipe(
      map(([loading, loadError, blocksLoading]) => !loadError && (loading || blocksLoading)),
    )

  private onDestroy$ = new Subject<void>()

  constructor(
    private snackbar: MatSnackBar,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit() {
    window.addEventListener('resize', (e: Event) => {
      this.resize$.next(e)
    })

    this.selectedPages$
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe((selectedPages) => this.selectionChange.next({
        selectedPages,
        totalPages: this.numberOfPages,
      }))

    if (!this.url) {
      return
    }

    this.pages$ = merge(
      from(this.processPDF()).pipe(
        catchError(() => {
          this.loadError$.next(true)
          this.loading$.next(false)

          return of([] as PdfPage[])
        }),
      ),
      this.relabelledPages$,
    )
    this.shouldShowLoadingThumbnals$ = this.loading$
      .pipe(
        combineLatestWith(this.pages$.pipe(startWith([]))),
        map(([loading, pages]) => loading && pages.length === 0),
      )

    this.resize$
      .pipe(
        takeUntil(this.onDestroy$),
        debounceTime(100),
        withLatestFrom(this.pages$),
      )
      .subscribe(([, pages]) => {
        const page = pages.find(({ pageNum }) => pageNum === this.currentPage)
        if (page?.pdfPage) {
          this.renderPage(page.pdfPage)
        }
      })
  }

  public ngOnChanges({ blocksLoading, fillMode, pagesData }: SimpleChanges) {
    if (blocksLoading) {
      this.blocksLoading$.next(Boolean(blocksLoading.currentValue))

      if (blocksLoading.currentValue) {
        this.snackbar.open(this.translate.instant('pdf-reader.status.ocr-processing'), '', { duration: 5000 })
      } else {
        this.snackbar.dismiss()
      }
    }

    if (fillMode && fillMode.currentValue !== fillMode.previousValue && this.pages$) {
      // re-render the blocks with the canvas size
      this.pages$.subscribe((pages) => {
        const page = pages.find(({ pageNum }) => pageNum === this.currentPage)
        if (page?.pdfPage) {
          this.renderPage(page.pdfPage)
        }
      })
    }

    if (pagesData && pagesData.currentValue) {
      const currentPage = this.pagesData.find((p) => p.isActive)?.pageNum
      if (currentPage) {
        this.currentPage = currentPage
      }
    }

    if (pagesData && this.loadedPages.length > 0) {
      this.relabelLoadedPages()
    }
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  public getThumbnailLabel(pageNum: number) {
    return (this.pagesData.find((page) => page.pageNum === pageNum)?.label || '').substring(0, 2)
  }

  public getThumbnailColor(pageNum: number) {
    return this.pagesData.find((page) => page.pageNum === pageNum)?.color || ''
  }

  public getThumbnailTooltip(pageNum: number) {
    return this.pagesData.find((page) => page.pageNum === pageNum)?.tooltip || ''
  }

  public onPageSelect({ page, event }: any) {
    let shouldLoad = true
    const selectedPages = this.selectedPages$.getValue()
    if (event?.ctrlKey || event?.metaKey) {
      if (selectedPages.has(page.pageNum)) {
        selectedPages.delete(page.pageNum)
        // If we are deselecting a page, we don't want to load it
        shouldLoad = false
      } else {
        selectedPages.add(page.pageNum)
      }
    } else if (event?.shiftKey) {
      const pages = Array.from(selectedPages)
      const start = Math.max(...pages)
      const newPages = Array.from({ length: Math.abs(page.pageNum - start) + 1 }, (_, i) => start + i)
      newPages.forEach((pageNum) => {
        if (!selectedPages.has(pageNum)) {
          selectedPages.add(pageNum)
        }
      })
    } else {
      selectedPages.clear()
      selectedPages.add(page.pageNum)
    }

    this.selectedPages$.next(selectedPages)

    if (shouldLoad) {
      this.loadPage(page)
    }

    this.activePageChange.emit(page.pageNum - 1)
  }

  public loadPage({ pageNum, pdfPage }: { pageNum: number, pdfPage: PDFPageProxy }) {
    this.currentPage = pageNum
    this.renderPage(pdfPage)

    this.loading$.next(false)
  }

  @HostListener('keydown', ['$event'])
  public onKeydown(event: KeyboardEvent) {
    const step = PAGE_STEP_BY_KEY[event.key]

    if (!step || event.ctrlKey || event.metaKey || event.altKey || this.isTextEntry(event.target)) {
      return
    }

    const page = this.loadedPages[this.currentPage - 1 + step]

    if (!page) {
      return
    }

    event.preventDefault()
    this.stepTo(page)
  }

  /**
   * Moves to a page without discarding a selection built with Ctrl or Shift for Move or Attach.
   *
   * A single selected page is the one being viewed, so it follows along; otherwise an action
   * would apply to a page the user has already stepped away from.
   */
  private stepTo(page: PdfPage) {
    const selectedPages = this.selectedPages$.getValue()

    if (selectedPages.size <= 1) {
      this.selectedPages$.next(new Set([page.pageNum]))
    }

    this.loadPage(page)
    this.activePageChange.emit(page.pageNum - 1)
  }

  /** Identification can name a page's patient after the document loaded, so labels are applied again. */
  private relabelLoadedPages() {
    this.loadedPages = this.loadedPages.map((page) => ({ ...page, ...this.thumbnailLabelling(page.pageNum) }))
    this.relabelledPages$.next(this.loadedPages)
  }

  private thumbnailLabelling(pageNum: number): Pick<PdfPage, 'label' | 'color' | 'tooltip'> {
    return {
      label: this.getThumbnailLabel(pageNum),
      color: this.getThumbnailColor(pageNum),
      tooltip: this.getThumbnailTooltip(pageNum),
    }
  }

  private isTextEntry(target: EventTarget | null): boolean {
    const element = target as HTMLElement | null

    return element?.isContentEditable === true || TEXT_ENTRY_TAGS.includes(element?.tagName ?? '')
  }

  /**
   * Draws a page, first cancelling any render still in progress. pdf.js refuses a second render into
   * a canvas it is still drawing, so held-down arrow keys would otherwise leave the wrong page up.
   */
  private renderPage(pdfPage: PDFPageProxy) {
    if (!this.canvasRef?.nativeElement?.parentElement) {
      return
    }

    const width = this.canvasRef.nativeElement.parentElement.clientWidth
    const height = window.innerHeight - 233 // max-height of the container + padding
    const pageDimensions = pdfPage.getViewport({ scale: 1 })

    let scale = 1
    if (this.fillMode === 'fit') {
      scale = height / pageDimensions.height
    }
    if (this.fillMode === 'fill') {
      scale = width / pageDimensions.width
    }

    const viewport = pdfPage.getViewport({ scale })

    this.canvasRef.nativeElement.width = viewport.width
    this.canvasRef.nativeElement.height = viewport.height
    this.canvasRef.nativeElement.style.width = `${viewport.width}px`
    this.canvasRef.nativeElement.style.height = `${viewport.height}px`

    this.renderTask?.cancel()
    this.renderTask = pdfPage.render({
      canvasContext: this.canvasRef?.nativeElement.getContext('2d')!,
      viewport,
    })
    this.renderTask.promise.catch((e: unknown) => {
      if ((e as Error)?.name !== 'RenderingCancelledException') {
        this.loadError$.next(true)
      }
    })

    this.width = viewport.width,
    this.height = viewport.height,
    this.cdr.detectChanges()
  }

  private async processPDF() {
    const pdf = await pdfjsLib.getDocument(this.url).promise
    const pages: PdfPage[] = []

    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const pdfPage = await pdf.getPage(pageNum)
      const thumbnail = await this.getPagePreview(pdfPage)
      pages.push({
        pageNum,
        pdfPage,
        thumbnail,
        ...this.thumbnailLabelling(pageNum),
      })
    }

    this.loadedPages = pages
    this.onPageSelect({ page: pages[this.currentPage - 1 || 0] })
    this.loading$.next(false)
    this.init.emit()

    return pages
  }

  private async getPagePreview(page: PDFPageProxy): Promise<string> {
    const thumbWidth = 200 * 2
    const viewport = page.getViewport({ scale: 1 })
    const canvas = document.createElement('canvas')
    canvas.width = thumbWidth
    canvas.height = (thumbWidth / viewport.width) * viewport.height
    const scale = Math.min(canvas.width / viewport.width, canvas.height / viewport.height)

    await page.render({
      canvasContext: canvas.getContext('2d')!,
      viewport: page.getViewport({ scale }),
    }).promise

    return canvas.toDataURL()
  }
}
